import { useCallback, useEffect, useRef } from 'react';
import { getCaptureId } from '@/lib/tracking';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Contactable = we have something we could actually reach them on later.
function isContactable(fd) {
  const email = (fd?.email || '').trim();
  const phone = (fd?.phone || '').replace(/\D/g, '');
  return EMAIL_RE.test(email) || phone.length >= 10;
}

/**
 * usePartialCapture — records a form entry that was started but not submitted,
 * so cold/abandoned contacts land in the "Cold Captures" pool for nurturing.
 * Opt-in per form (formConfig.capture_partials).
 *
 * CHECKPOINT-BASED (not per-keystroke) to keep request volume low across many
 * tenants: the form calls the returned `capture()` at meaningful moments (a
 * step advance, a field blur), plus one safety beacon fires on tab-hide. Every
 * send is de-duplicated against the last one, so repeat calls cost nothing.
 * Does nothing after a successful submit (that becomes a real lead instead).
 *
 * @returns {{ capture: () => void }}
 */
export function usePartialCapture({ formConfig, formData, tracking, submitted }) {
  const enabled = !!formConfig?.capture_partials && !!formConfig?.id;
  // Keep the latest values in a ref so the once-registered listener and the
  // stable capture() callback always read current data without re-subscribing.
  const stateRef = useRef({ formData, submitted, tracking });
  stateRef.current = { formData, submitted, tracking };
  const lastSig = useRef('');

  const build = () => {
    const { formData: fd, tracking: tk } = stateRef.current;
    const cookies = tk?.get ? tk.get() : {};
    return {
      session_key: getCaptureId(),
      first_name: fd.first_name || '',
      last_name: fd.last_name || '',
      email: fd.email || '',
      phone: fd.phone || '',
      custom_data: fd.custom_data || {},
      cookies_data: cookies,
      source: cookies.utm_source || (typeof window !== 'undefined' ? window.location.hostname : ''),
    };
  };

  const sigOf = (p) =>
    JSON.stringify([p.first_name, p.last_name, p.email, p.phone, p.custom_data]);

  const post = (payload, beacon) => {
    const url = `${API_URL}/api/forms/${formConfig.id}/partial`;
    try {
      if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch { /* never surface capture errors */ }
  };

  // Explicit checkpoint. Call on step-advance / field blur. Skips when disabled,
  // already submitted, not contactable, or unchanged since the last send.
  const capture = useCallback(() => {
    if (!enabled) return;
    const { formData: fd, submitted: done } = stateRef.current;
    if (done || !isContactable(fd)) return;
    const payload = build();
    const sig = sigOf(payload);
    if (sig === lastSig.current) return;
    lastSig.current = sig;
    post(payload, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, formConfig?.id]);

  // Safety net: a single beacon on tab-hide / unload, only if the current data
  // differs from whatever we last sent at a checkpoint.
  useEffect(() => {
    if (!enabled) return;
    const flush = () => {
      const { formData: fd, submitted: done } = stateRef.current;
      if (done || !isContactable(fd)) return;
      const payload = build();
      const sig = sigOf(payload);
      if (sig === lastSig.current) return;
      lastSig.current = sig;
      post(payload, true);
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { capture };
}
